import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Autocomplete } from "@/components/ui/autocomplete";
import { useFocusMunicipalitySearch, type MunicipalityOption } from "@/hooks/useFocusMunicipalitySearch";

interface FocusMunicipalityAutocompleteProps {
  value?: string | null;
  state?: string | null;
  onChange: (value: string, municipality?: MunicipalityOption) => void;
  placeholder: string;
  disabled?: boolean;
}

export function FocusMunicipalityAutocomplete({
  value,
  state,
  onChange,
  placeholder,
  disabled,
}: FocusMunicipalityAutocompleteProps) {
  const { t } = useTranslation("erp");
  const [displayText, setDisplayText] = useState(value ?? "");
  const normalizedValue = value ?? "";

  const exactQuery = useFocusMunicipalitySearch({
    query: normalizedValue,
    state,
    enabled: normalizedValue.length === 7,
  });

  const searchQuery = useFocusMunicipalitySearch({
    query: displayText,
    state,
    enabled: !disabled,
  });

  const options = useMemo(
    () =>
      (searchQuery.data?.options ?? []).map((o) => ({
        value: o.value,
        label: o.label,
      })),
    [searchQuery.data],
  );

  useEffect(() => {
    if (!normalizedValue) {
      setDisplayText("");
      return;
    }
    const match = exactQuery.data?.options?.find((o) => o.value === normalizedValue);
    setDisplayText(match?.label ?? normalizedValue);
  }, [normalizedValue, exactQuery.data]);

  const isLoading = searchQuery.isFetching || exactQuery.isFetching;

  return (
    <div className="relative">
      <Autocomplete
        options={options}
        value={displayText}
        selectedValue={normalizedValue}
        onChange={(text) => {
          setDisplayText(text);
          if (!text.trim()) onChange("");
        }}
        onSelect={(option) => {
          const matched = searchQuery.data?.options?.find((o) => o.value === option.value);
          setDisplayText(option.label);
          onChange(option.value, matched);
        }}
        placeholder={placeholder}
        disabled={disabled}
        disableLocalFilter
        emptyMessage={
          isLoading
            ? t("common.loading")
            : displayText.trim().length < 2
            ? t("fiscalSettings.general.municipalitySearch.typeToSearch")
            : t("fiscalSettings.general.municipalitySearch.noResults")
        }
      />
      {isLoading && (
        <Loader2 className="pointer-events-none absolute right-8 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />
      )}
    </div>
  );
}
